// src/modules/units/tyres/views/catalogue/tyrecatalogue.js
//
// Active-customer tyre catalogue CRUD. A catalogue entry is deliberately one
// composite row (manufacturer + model + tread + tyre specification/life defaults),
// matching the backend schema. The three identity fields use datalist-backed
// typeaheads: they suggest values already used by this customer while still
// allowing an administrator to add a genuinely new value.

import {
  fetchTyreCatalogue,
  createTyreCatalogueEntry,
  updateTyreCatalogueEntry,
  deleteTyreCatalogueEntry
} from '../tyre-catalogue.api.js';
import { icon } from '../../shared/tyre-icons.js';
import { esc } from '../../shared/tyre-format.js';
import { onTyreEvent, TYRE_EVENTS } from '../../shared/tyre-events.js';
import { sameCatalogueIdentity } from '../../shared/tyre-catalogue-match.js';
import {
  TYRE_SIZES, TYRE_TYPES, TYRE_APPLICATIONS, TYRE_GRADES
} from '../../shared/tyre-vocab.js';

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

const norm = (v) => String(v || '').trim().toLocaleLowerCase();

function uniqueValues(rows, key) {
  const seen = new Map();
  rows.forEach((row) => {
    const value = String(row?.[key] || '').trim();
    const n = norm(value);
    if (value && !seen.has(n)) seen.set(n, value);
  });
  return Array.from(seen.values()).sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  );
}

function setDatalist(listEl, values) {
  listEl.innerHTML = values.map((value) => `<option value="${esc(value)}"></option>`).join('');
}

function selectOptions(values, placeholder) {
  return [`<option value="">${esc(placeholder || 'Select…')}</option>`]
    .concat(values.map((value) => `<option value="${esc(value)}">${esc(value)}</option>`))
    .join('');
}

function setSelectValue(select, value) {
  if (!select) return;
  const text = String(value ?? '').trim();
  if (!text) {
    select.value = '';
    return;
  }
  const exists = Array.from(select.options).some((option) => option.value === text);
  if (!exists) select.add(new Option(text, text));
  select.value = text;
}

function entrySub(entry) {
  const bits = [];
  if (entry.treadPatternCode) bits.push(`Tread ${entry.treadPatternCode}`);
  if (entry.tyreSize) bits.push(entry.tyreSize);
  if (entry.tyreApplication) bits.push(entry.tyreApplication);
  if (entry.tyreType) bits.push(entry.tyreType);
  if (entry.tyreGrade) bits.push(entry.tyreGrade);
  if (entry.manufacturerNominalTreadDepth !== null &&
      entry.manufacturerNominalTreadDepth !== undefined &&
      entry.manufacturerNominalTreadDepth !== '') {
    bits.push(`${entry.manufacturerNominalTreadDepth} mm nominal tread`);
  }
  if (entry.manufacturerExpectedMileage !== null &&
      entry.manufacturerExpectedMileage !== undefined &&
      entry.manufacturerExpectedMileage !== '') {
    bits.push(`${Number(entry.manufacturerExpectedMileage).toLocaleString('en-ZA')} km expected`);
  }
  if (entry.minimumTreadForRetread !== null &&
      entry.minimumTreadForRetread !== undefined &&
      entry.minimumTreadForRetread !== '') {
    bits.push(`${entry.minimumTreadForRetread} mm min retread`);
  }
  return bits.join(' · ') || 'No tyre specification';
}

export function renderTyreCatalogue(viewEl) {
  let disposed = false;
  let loading = false;
  let entries = [];
  let editingId = null;
  let deletingId = null;

  const root = el(`
    <div class="tm-catalogue">
      <section class="tm-catalogue-editor">
        <div class="tm-catalogue-editor-head">
          <div>
            <h2 class="tm-catalogue-editor-title" data-editor-title>Add catalogue entry</h2>
            <p class="tm-catalogue-editor-sub">Catalogue entries belong to the active customer. Only administrators can add, edit or delete them.</p>
          </div>
          <span class="tm-catalogue-editor-ic">${icon('catalogue')}</span>
        </div>

        <div class="tm-catalogue-grid">
          <label class="tm-catalogue-field">
            <span>Manufacturer / brand <span class="tm-req">*</span></span>
            <input class="tm-input" type="text" autocomplete="off" list="tmCatalogueManufacturers" data-catalogue-field="manufacturerName" />
            <datalist id="tmCatalogueManufacturers" data-catalogue-list="manufacturerName"></datalist>
          </label>

          <label class="tm-catalogue-field">
            <span>Model <span class="tm-req">*</span></span>
            <input class="tm-input" type="text" autocomplete="off" list="tmCatalogueModels" data-catalogue-field="modelName" />
            <datalist id="tmCatalogueModels" data-catalogue-list="modelName"></datalist>
          </label>

          <label class="tm-catalogue-field">
            <span>Tread pattern code</span>
            <input class="tm-input" type="text" autocomplete="off" list="tmCatalogueTreads" data-catalogue-field="treadPatternCode" />
            <datalist id="tmCatalogueTreads" data-catalogue-list="treadPatternCode"></datalist>
          </label>

          <label class="tm-catalogue-field">
            <span>Tyre size</span>
            <select class="tm-input" data-catalogue-field="tyreSize">
              ${selectOptions(TYRE_SIZES, 'Select a tyre size')}
            </select>
          </label>

          <label class="tm-catalogue-field">
            <span>Application</span>
            <select class="tm-input" data-catalogue-field="tyreApplication">
              ${selectOptions(TYRE_APPLICATIONS, 'Select an application')}
            </select>
          </label>

          <label class="tm-catalogue-field">
            <span>Type</span>
            <select class="tm-input" data-catalogue-field="tyreType">
              ${selectOptions(TYRE_TYPES, 'Select a tyre type')}
            </select>
          </label>

          <label class="tm-catalogue-field">
            <span>Grade</span>
            <select class="tm-input" data-catalogue-field="tyreGrade">
              ${selectOptions(TYRE_GRADES, 'Select a grade')}
            </select>
          </label>

          <label class="tm-catalogue-field">
            <span>Manufacturer nominal tread</span>
            <div class="tm-input-affix">
              <input class="tm-input" type="number" inputmode="decimal" step="0.01" min="0" data-catalogue-field="manufacturerNominalTreadDepth" />
              <span class="tm-affix tm-affix-post">mm</span>
            </div>
          </label>

          <label class="tm-catalogue-field">
            <span>Manufacturer expected mileage</span>
            <div class="tm-input-affix">
              <input class="tm-input" type="number" inputmode="numeric" step="1" min="0" data-catalogue-field="manufacturerExpectedMileage" />
              <span class="tm-affix tm-affix-post">km</span>
            </div>
          </label>

          <label class="tm-catalogue-field">
            <span>Minimum tread for retread</span>
            <div class="tm-input-affix">
              <input class="tm-input" type="number" inputmode="decimal" step="0.01" min="0" data-catalogue-field="minimumTreadForRetread" />
              <span class="tm-affix tm-affix-post">mm</span>
            </div>
          </label>

          <label class="tm-catalogue-field tm-catalogue-field-wide">
            <span>Tread pattern description</span>
            <textarea class="tm-input tm-textarea" rows="2" data-catalogue-field="treadPatternDescription"></textarea>
          </label>

          <label class="tm-catalogue-active">
            <input type="checkbox" data-catalogue-field="isActive" checked />
            <span>Active — show this entry as a choice when adding/editing tyres</span>
          </label>
        </div>

        <div class="tm-catalogue-msg" data-catalogue-msg hidden></div>
        <div class="tm-catalogue-editor-actions">
          <button type="button" class="tm-btn-secondary" data-cancel-edit hidden>Cancel edit</button>
          <button type="button" class="tm-btn-primary" data-save-entry>Add entry</button>
        </div>
      </section>

      <section class="tm-catalogue-list-card">
        <div class="tm-catalogue-list-head">
          <div>
            <h2>Tyre catalogue</h2>
            <p>Manufacturer, model, tread and tyre specification defaults available to this customer.</p>
          </div>
          <span data-catalogue-count></span>
        </div>
        <div class="tm-catalogue-list" data-catalogue-list></div>
      </section>
    </div>
  `);

  const listEl = root.querySelector('[data-catalogue-list]:not(datalist)');
  const countEl = root.querySelector('[data-catalogue-count]');
  const titleEl = root.querySelector('[data-editor-title]');
  const msgEl = root.querySelector('[data-catalogue-msg]');
  const saveBtn = root.querySelector('[data-save-entry]');
  const cancelBtn = root.querySelector('[data-cancel-edit]');
  const fields = Object.fromEntries(
    Array.from(root.querySelectorAll('[data-catalogue-field]'))
      .map((input) => [input.dataset.catalogueField, input])
  );
  const lists = Object.fromEntries(
    Array.from(root.querySelectorAll('datalist[data-catalogue-list]'))
      .map((list) => [list.dataset.catalogueList, list])
  );

  function setMsg(text, kind = '') {
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    msgEl.className = `tm-catalogue-msg${kind ? ` is-${kind}` : ''}`;
  }

  function setBusy(on) {
    saveBtn.disabled = on;
    cancelBtn.disabled = on;
    Object.values(fields).forEach((input) => { input.disabled = on; });
  }

  function refreshTypeaheads() {
    setDatalist(lists.manufacturerName, uniqueValues(entries, 'manufacturerName'));

    const manufacturer = norm(fields.manufacturerName.value);
    const modelRows = manufacturer
      ? entries.filter((entry) => norm(entry.manufacturerName) === manufacturer)
      : entries;
    setDatalist(lists.modelName, uniqueValues(modelRows, 'modelName'));

    const model = norm(fields.modelName.value);
    const treadRows = model
      ? modelRows.filter((entry) => norm(entry.modelName) === model)
      : modelRows;
    setDatalist(lists.treadPatternCode, uniqueValues(treadRows, 'treadPatternCode'));
  }

  function clearEditor({ keepMessage = false } = {}) {
    editingId = null;
    fields.manufacturerName.value = '';
    fields.modelName.value = '';
    fields.treadPatternCode.value = '';
    fields.treadPatternDescription.value = '';
    setSelectValue(fields.tyreSize, '');
    setSelectValue(fields.tyreApplication, '');
    setSelectValue(fields.tyreType, '');
    setSelectValue(fields.tyreGrade, '');
    fields.manufacturerNominalTreadDepth.value = '';
    fields.manufacturerExpectedMileage.value = '';
    fields.minimumTreadForRetread.value = '';
    fields.isActive.checked = true;
    titleEl.textContent = 'Add catalogue entry';
    saveBtn.textContent = 'Add entry';
    cancelBtn.hidden = true;
    if (!keepMessage) setMsg('');
    refreshTypeaheads();
  }

  function startEdit(entry) {
    editingId = entry.tyreCatalogueEntryId;
    deletingId = null;
    fields.manufacturerName.value = entry.manufacturerName || '';
    fields.modelName.value = entry.modelName || '';
    fields.treadPatternCode.value = entry.treadPatternCode || '';
    fields.treadPatternDescription.value = entry.treadPatternDescription || '';
    setSelectValue(fields.tyreSize, entry.tyreSize || '');
    setSelectValue(fields.tyreApplication, entry.tyreApplication || '');
    setSelectValue(fields.tyreType, entry.tyreType || '');
    setSelectValue(fields.tyreGrade, entry.tyreGrade || '');
    fields.manufacturerNominalTreadDepth.value = entry.manufacturerNominalTreadDepth ?? '';
    fields.manufacturerExpectedMileage.value = entry.manufacturerExpectedMileage ?? '';
    fields.minimumTreadForRetread.value = entry.minimumTreadForRetread ?? '';
    fields.isActive.checked = entry.isActive !== false;
    titleEl.textContent = 'Edit catalogue entry';
    saveBtn.textContent = 'Save changes';
    cancelBtn.hidden = false;
    setMsg('');
    refreshTypeaheads();
    renderList();
    root.scrollIntoView({ behavior: 'smooth', block: 'start' });
    fields.manufacturerName.focus();
    fields.manufacturerName.select();
  }

  function rowMarkup(entry) {
    const id = esc(entry.tyreCatalogueEntryId);
    const isEditing = String(editingId) === String(entry.tyreCatalogueEntryId);
    const isDeleting = String(deletingId) === String(entry.tyreCatalogueEntryId);
    const active = entry.isActive !== false;

    return `
      <div class="tm-catalogue-row${isEditing ? ' is-editing' : ''}${active ? '' : ' is-inactive'}" data-catalogue-id="${id}">
        <span class="tm-catalogue-row-ic">${icon('catalogue')}</span>
        <div class="tm-catalogue-row-main">
          <div class="tm-catalogue-row-title">
            <span class="tm-catalogue-manufacturer">${esc(entry.manufacturerName || 'Unnamed manufacturer')}</span>
            <span class="tm-catalogue-model">${esc(entry.modelName || 'Unnamed model')}</span>
            ${active ? '' : '<span class="tm-badge tm-tone-slate">Inactive</span>'}
          </div>
          <span class="tm-catalogue-row-sub">${esc(entrySub(entry))}</span>
          ${entry.treadPatternDescription ? `<span class="tm-catalogue-row-description">${esc(entry.treadPatternDescription)}</span>` : ''}
        </div>
        ${isDeleting ? `
          <div class="tm-catalogue-delete-confirm">
            <span>Delete this entry?</span>
            <button type="button" class="tm-btn-link" data-cancel-delete>Cancel</button>
            <button type="button" class="tm-btn-danger-small" data-confirm-delete>Delete</button>
          </div>` : `
          <div class="tm-catalogue-row-actions">
            <button type="button" class="tm-btn-link" data-edit-entry>Edit</button>
            <button type="button" class="tm-btn-link tm-btn-link-danger" data-delete-entry>Delete</button>
          </div>`}
      </div>`;
  }

  function renderList() {
    if (loading) {
      countEl.textContent = '';
      listEl.innerHTML = '<div class="tm-catalogue-state">Loading tyre catalogue…</div>';
      return;
    }

    countEl.textContent = `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`;

    if (!entries.length) {
      listEl.innerHTML = '<div class="tm-catalogue-state">No catalogue entries yet. Add the first manufacturer / model combination above.</div>';
      return;
    }

    listEl.innerHTML = entries.map(rowMarkup).join('');
  }

  async function loadList() {
    loading = true;
    renderList();
    const res = await fetchTyreCatalogue({ sort: 'manufacturerName,modelName' });
    if (disposed) return;
    loading = false;

    if (!res.success) {
      entries = [];
      countEl.textContent = '';
      listEl.innerHTML = `
        <div class="tm-catalogue-state is-error">
          ${esc(res.message || 'Could not load the tyre catalogue.')}
          <button type="button" class="tm-btn-link" data-retry-catalogue>Retry</button>
        </div>`;
      refreshTypeaheads();
      return;
    }

    entries = Array.isArray(res.data?.results) ? res.data.results : [];
    refreshTypeaheads();
    renderList();
  }

  function payloadFromEditor() {
    return {
      manufacturerName: fields.manufacturerName.value.trim(),
      modelName: fields.modelName.value.trim(),
      treadPatternCode: fields.treadPatternCode.value.trim(),
      treadPatternDescription: fields.treadPatternDescription.value.trim(),
      tyreSize: fields.tyreSize.value,
      tyreApplication: fields.tyreApplication.value,
      tyreType: fields.tyreType.value,
      tyreGrade: fields.tyreGrade.value,
      manufacturerNominalTreadDepth: fields.manufacturerNominalTreadDepth.value,
      manufacturerExpectedMileage: fields.manufacturerExpectedMileage.value,
      minimumTreadForRetread: fields.minimumTreadForRetread.value,
      isActive: fields.isActive.checked
    };
  }

  async function saveEntry() {
    const payload = payloadFromEditor();
    if (!payload.manufacturerName) {
      setMsg('Enter a manufacturer / brand.', 'error');
      fields.manufacturerName.focus();
      return;
    }
    if (!payload.modelName) {
      setMsg('Enter a model.', 'error');
      fields.modelName.focus();
      return;
    }
    const numericRules = [
      ['manufacturerNominalTreadDepth', 'Enter a valid nominal tread depth.'],
      ['manufacturerExpectedMileage', 'Enter a valid manufacturer expected mileage.'],
      ['minimumTreadForRetread', 'Enter a valid minimum tread for retread.']
    ];
    for (const [key, message] of numericRules) {
      if (payload[key] !== '' && (!Number.isFinite(Number(payload[key])) || Number(payload[key]) < 0)) {
        setMsg(message, 'error');
        fields[key].focus();
        return;
      }
    }

    // Identity rule shared with the importer and the tyre form
    // (tyre-catalogue-match.js); sizes compare loosely, so 315/80R22.5 and
    // 315/80/R22.5 are the same specification.
    const duplicate = entries.find((entry) =>
      String(entry.tyreCatalogueEntryId) !== String(editingId || '') &&
      sameCatalogueIdentity(entry, payload)
    );
    if (duplicate) {
      setMsg('That catalogue specification already exists for this customer.', 'error');
      fields.treadPatternCode.focus();
      return;
    }

    setBusy(true);
    setMsg(editingId ? 'Saving changes…' : 'Adding catalogue entry…');

    const wasEdit = !!editingId;
    const res = wasEdit
      ? await updateTyreCatalogueEntry(editingId, payload)
      : await createTyreCatalogueEntry(payload);

    if (disposed) return;
    setBusy(false);

    if (!res.success) {
      const adminHint = res.status === 403 ? ' Administrator access is required.' : '';
      setMsg((res.message || 'Could not save the catalogue entry.') + adminHint, 'error');
      return;
    }

    clearEditor({ keepMessage: true });
    setMsg(wasEdit ? 'Catalogue entry updated.' : 'Catalogue entry added.', 'ok');
    fields.manufacturerName.focus();
  }

  async function confirmDelete(entryId) {
    const entry = entries.find((row) => String(row.tyreCatalogueEntryId) === String(entryId));
    if (!entry) return;

    const res = await deleteTyreCatalogueEntry(entryId);
    if (disposed) return;

    if (!res.success) {
      deletingId = null;
      const adminHint = res.status === 403 ? ' Administrator access is required.' : '';
      setMsg((res.message || 'Could not delete the catalogue entry.') + adminHint, 'error');
      renderList();
      return;
    }

    if (String(editingId) === String(entryId)) clearEditor({ keepMessage: true });
    deletingId = null;
    setMsg(`Catalogue entry “${entry.manufacturerName} · ${entry.modelName}” deleted.`, 'ok');
  }

  saveBtn.addEventListener('click', saveEntry);
  cancelBtn.addEventListener('click', () => clearEditor());

  ['manufacturerName', 'modelName', 'treadPatternCode'].forEach((key) => {
    fields[key].addEventListener('input', refreshTypeaheads);
    fields[key].addEventListener('change', refreshTypeaheads);
  });

  Object.values(fields).forEach((input) => {
    if (input.type === 'checkbox' || input.tagName === 'TEXTAREA') return;
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); saveEntry(); }
    });
  });

  listEl.addEventListener('click', (e) => {
    if (e.target.closest('[data-retry-catalogue]')) { loadList(); return; }

    const row = e.target.closest('[data-catalogue-id]');
    if (!row) return;
    const id = row.dataset.catalogueId;
    const entry = entries.find((item) => String(item.tyreCatalogueEntryId) === String(id));

    if (e.target.closest('[data-edit-entry]') && entry) { startEdit(entry); return; }
    if (e.target.closest('[data-delete-entry]')) { deletingId = id; renderList(); return; }
    if (e.target.closest('[data-cancel-delete]')) { deletingId = null; renderList(); return; }
    if (e.target.closest('[data-confirm-delete]')) confirmDelete(id);
  });

  viewEl.replaceChildren(root);
  const offCatalogue = onTyreEvent(TYRE_EVENTS.CATALOGUE_CHANGED, loadList);
  loadList();
  fields.manufacturerName.focus();

  return () => {
    disposed = true;
    offCatalogue();
  };
}
